/**
 * Calculate the percentages, rounded to the given number of decimal places, of any array of values
 * Example: returns 0.999 for 99.9% if decimalPlaces is 1
 * if the percentages don't sum to 1, use Largest remainder method to distribute the missing decimal points
 * That means: percentages with the next largest decimal before rounding get the missing values descimal points
 * see: https://en.wikipedia.org/wiki/Largest_remainder_method
 *
 * @param {Number[]} values The values, do not have to be 100 or 1 in total.
 * @param {Integer} decimalPlaces The number of decimals places.
 * @returns The input values with percentage and percentageRounded values in the same order as the input values were.
 */
export function roundTo100Percent(
    values: number[],
    decimalPlaces: number = 1
): {
    value: number;
    percentage: number;
    percentageRounded: number;
}[] {
    const factorForRounding = Math.pow(10, decimalPlaces) * 100;
    const shiftedDecimalPlaces = decimalPlaces + 2; // account for percentages being in the range of 0.0-1.0
    const floor = (v) => toDecimals(v, shiftedDecimalPlaces, Math.floor);
    const round = (v) => toDecimals(v, shiftedDecimalPlaces, Math.round);

    const total = values.reduce((prev, curr) => prev + curr, 0);

    const percentages = values.map((d, i) => ({
        percentage: d / total,
        value: d,
        index: i,
    }));

    const roundedValues = percentages.map(({ percentage }) =>
        floor(percentage)
    );

    const diff =
        1 - round(roundedValues.reduce((prev, curr) => prev + curr, 0));

    const sortedByDecimalPart = percentages.sort((a, b) => {
        const aDiff = Math.abs(floor(a.percentage) - a.percentage);
        const bDiff = Math.abs(floor(b.percentage) - b.percentage);
        const compareResult = bDiff - aDiff;
        return compareResult;
    });

    return sortedByDecimalPart
        .map((d, i) => {
            return {
                percentageRounded:
                    i + 1 < diff * factorForRounding
                        ? floor(d.percentage + 1 / factorForRounding)
                        : floor(d.percentage),
                ...d,
            };
        })
        .sort((a, b) => a.index - b.index);
}

/**
 * Decimal shifting and round/floor/ceil of a number. Source: https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Math/floor
 * Only reliable way because everything else leads to JS rounding quirks
 *
 * @param {Number}  value The number.
 * @param {Integer} places   The number of decimals places.
 * @param {Function} roundFn     Function to apply on shifted values. Try Math.round, Math.ceil, Math.floor.
 * @returns {Number} The floored value with the given number of decimals.
 */
function toDecimals(
    value: number,
    places: number,
    roundFn: (x: number) => number
): number {
    // If the places is undefined or zero...
    if (typeof places === "undefined" || +places === 0) {
        return roundFn(value);
    }
    let newValue = +value;
    let newPlaces = -places;
    // If the value is not a number or the places is not an integer...
    if (
        isNaN(newValue) ||
        !(typeof newPlaces === "number" && newPlaces % 1 === 0)
    ) {
        return NaN;
    }
    // Shift
    const splitValue = newValue.toString().split("e");
    const shiftedValue = roundFn(
        +(
            splitValue[0] +
            "e" +
            (splitValue[1] != null ? +splitValue[1] - newPlaces : -newPlaces)
        )
    );
    // Shift back
    const splitShiftedValue = shiftedValue.toString().split("e");
    return +(
        splitShiftedValue[0] +
        "e" +
        (splitShiftedValue[1] != null
            ? +splitShiftedValue[1] + newPlaces
            : newPlaces)
    );
}
